import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  pieArcLabelClasses,
  legendClasses,
  PiePlot,
  ResponsiveChartContainer,
  ChartsTooltip,
  chartsTooltipClasses,
} from "@mui/x-charts";

/**
 * DistributionPie — the donut used by the Clients / Linguists number overlays.
 *
 * This is the SAME chart the old Dashboard rendered (identical series config,
 * arc labels, tooltip and slice styling), extracted so the two overlays cannot
 * drift apart from each other or from the screen they replaced.
 *
 * Data shape: [{ id, value, label, color }] — a slice with value 0 is kept so
 * the colour mapping stays stable, but it renders as nothing, exactly like
 * before.
 */
const DistributionPie = ({ data, seriesId, emptyLabel = "No data" }) => {
  const theme = useTheme();

  const total = (data || []).reduce(
    (sum, item) => sum + (Number(item?.value) || 0),
    0,
  );

  if (total <= 0) {
    return (
      <Box
        sx={{
          height: 250,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyLabel}
        </Typography>
      </Box>
    );
  }

  // Percent of the whole, rounded like the original (guarded against /0 by the
  // early return above).
  const getArcLabel = (params) => `${((params.value / total) * 100).toFixed(0)}%`;

  return (
    <ResponsiveChartContainer
      series={[
        {
          type: "pie",
          data,
          id: seriesId,
          highlightScope: { faded: "global", highlighted: "item" },
          animated: true,
          arcLabel: getArcLabel,
          arcLabelMinAngle: 20,
          innerRadius: 50,
          outerRadius: 100,
          paddingAngle: 2,
          cornerRadius: 10,
        },
      ]}
      margin={{ top: 100, bottom: 100, left: 0, right: 0 }}
      sx={{
        "& path": {
          stroke: "transparent",
          strokeWidth: 5,
        },
        [`& .${pieArcLabelClasses.root}`]: {
          fill: theme.palette.background.default,
          fontSize: 14,
          fontWeight: "bold",
        },
        [`& .${legendClasses.mark}`]: {
          ry: 6,
        },
      }}
    >
      <ChartsTooltip
        trigger="item"
        labelFormatter={(itemData) => itemData.label || ""}
        valueFormatter={(value, itemData) =>
          `${value}${itemData.format === "percentage" ? "%" : ""}`
        }
        // 2026-11-03 (Amine): x-charts ships a DARK tooltip regardless of the
        // app theme, which read as a foreign element in light mode. These map
        // every tooltip surface onto the portal palette, so it follows the
        // theme in both modes instead of being hardcoded either way.
        sx={{
          [`& .${chartsTooltipClasses.paper}`]: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "12px",
            boxShadow: theme.shadows[1],
          },
          [`& .${chartsTooltipClasses.labelCell}`]: {
            color: theme.palette.text.secondary,
          },
          [`& .${chartsTooltipClasses.valueCell}`]: {
            color: theme.palette.text.primary,
            fontWeight: 600,
          },
          [`& .${chartsTooltipClasses.mark}`]: {
            borderRadius: "4px",
          },
        }}
      />
      <PiePlot />
    </ResponsiveChartContainer>
  );
};

export default DistributionPie;
